import { isStatOrUrgentPriority, SchedulingPriority } from "@/packages/shared/src/models/referral";
import { createAction, ActionPriority } from "@/src/lib/ehr/ecw/webecw/endpoints/actions";
import type { EcwSession } from "@/src/lib/ehr/ecw/webecw/session";
import { SchedulingIneligibilityReason } from "@/src/models/scheduling-recommendation";

export function buildPimaEscalationActionText(reasonLabel: string, detail?: string): { subject: string; note: string } {
  const subject = `Referral - ${reasonLabel}`;
  const note = detail
    ? `Please schedule referral, the patient is ${reasonLabel}. ${detail}`
    : `Please schedule referral, the patient is ${reasonLabel}.`;

  return { subject, note };
}

// eCW staff *display names* from Faith's provider roster CSV ("Staff User" column) — NOT resolved
// numeric eCW user IDs. createAction() requires a numeric assignedToId; there is no existing code
// path anywhere in the repo that resolves an eCW staff display name to that ID. Do not call
// escalatePimaReferral() against a live eCW session until this map is replaced with real IDs, or
// until callers resolve assignedToId some other way.
export const PIMA_STAFF_USER_BY_NPI: Record<string, string> = {
  "1487632212": "Algeo,Staff",
  "1679551485": "Bejarano,Staff",
  "1023011269": "Decena,Staff",
  "1679551386": "Desai,Staff",
  "1912962804": "Faitelson,Staff",
  "1952562647": "Gheewala,Staff",
  "1326350562": "Gopalakrishnan,Staff",
  "1699076596": "Kansagra,Staff",
  "1164425096": "Koshkarian,Staff",
  "1457670440": "Menezes,Staff",
  "1639147382": "Molls,Staff",
  "1043298656": "Morales,Staff",
  "1477531754": "Mostafizi,Staff",
  "1417077603": "Munjal,Staff",
  "1679551295": "Myer,Staff",
  "1548248057": "Peress,Staff",
  "1194727438": "Spooner,Staff",
  "1487632659": "Tirrito,Staff",
  "1093793267": "Tuli,Staff",
  "1891083812": "Waggoner,Staff",
  "1225031131": "Watson,Staff",
  "1306285325": "Balderman,Staff",
  "1811004435": "BermanScott,Staff",
  "1609980499": "Mendoza,Staff",
  "1538426069": "Sabat,Staff",
  "1477531713": "Shaikh,Staff",
  "1972123743": "Chan,Staff",
  "1639651896": "Cristelli,Staff",
  "1003782509": "Elias,Staff",
  "1285015503": "Hackett,Staff",
  "1326270059": "Keating,Staff",
  "1124625140": "Kemnitz,Staff",
  "1730672262": "Marquis,Staff",
  "1649904731": "McCrory,Staff",
  "1649611336": "Pielacha,Staff",
  "1235433426": "Strout,Staff",
  "1881461861": "Versluis,Staff",
  "1902782600": "Lucio,Staff",
  "1962062620": "Weaver,Staff",
};

// Action priority is based on the referral's scheduling urgency, not the escalation reason:
// Routine -> "2", STAT/Urgent -> "3" (per Faith). A null/missing priority is treated as Routine.
function getActionPriorityForSchedulingPriority(schedulingPriority: SchedulingPriority | null): ActionPriority {
  return isStatOrUrgentPriority(schedulingPriority) ? ActionPriority.enum["3"] : ActionPriority.enum["2"];
}

export interface EscalatePimaReferralParams {
  // Must be an EcwSession authenticated as Sibel Sayiner's eCW account (per Faith) — createdById/
  // createdByName below are derived from the session's own trUserId/trUserName rather than taken
  // as separate params, so escalations are always attributed to whichever account the session is.
  session: EcwSession;
  mrn: string;
  reason: SchedulingIneligibilityReason;
  // Additional human-readable context for the Action's notes, e.g. the specific diagnosis text
  // that triggered a POTS/cardiac-clearance escalation.
  detail?: string;
  schedulingPriority: SchedulingPriority | null;
  assignedToId: string;
  assignedToName: string;
  facilityId: string;
  facilityName: string;
  timeZone: string;
}

// Creates the eCW Action that represents an escalated (not-scheduled) referral, per
// configuration/groups/ushv-pima/rules/system-instructions.md's escalation policy. Returns the
// created Action's ID.
export async function escalatePimaReferral(params: EscalatePimaReferralParams): Promise<string> {
  const { subject, note } = buildPimaEscalationActionText(params.reason, params.detail);

  return createAction(params.session, {
    mrn: params.mrn,
    subject,
    assignedToId: params.assignedToId,
    assignedToName: params.assignedToName,
    createdById: params.session.trUserId,
    createdByName: params.session.trUserName,
    facilityId: params.facilityId,
    facilityName: params.facilityName,
    note,
    timeZone: params.timeZone,
    priority: getActionPriorityForSchedulingPriority(params.schedulingPriority),
  });
}
